/**
 * "What's New" entries for the Sessions page.
 *
 * This is the single edit point per release: add an entry at the TOP when a
 * feature ships, and it appears in the rail above Recent Sessions with a NEW
 * dot until the driver has seen it.
 *
 * Keep it to the handful of things worth interrupting someone for. The list is
 * a discovery aid, not a changelog — every blurb should say *where the thing
 * lives*, because most of these sit behind the View menu of a loaded session
 * and that is exactly why nobody found them.
 *
 * Fields:
 *   id      stable slug — it is the key in the "already seen" set, so never
 *           reuse or rename one, or every driver sees the entry again.
 *   date    "YYYY-MM"; rendered as "Sep 2026" and used for ordering.
 *   title   short feature name, as it appears in the UI it points at.
 *   blurb   one or two sentences. What it does, then where to find it.
 *   icon    a lucide-react export name (see ICONS in WhatsNew.jsx — an icon
 *           must be added there too, since we don't import the whole set).
 *   beta    true renders the shared BETA pill after the title — never write
 *           "(beta)" into the title itself.
 *   help    { page, panel } of the Help article to link to. The link is only
 *           rendered when an article with that page+panel actually exists, so
 *           an entry whose article has not been written yet degrades to a card
 *           with no link rather than opening an empty Help panel.
 *   poster  optional still, shown as a 16:9 thumbnail on the featured card.
 *   video   optional [webm, mp4] sources, mounted only when the poster is
 *           clicked so a page load never fetches them.
 */
export const WHATS_NEW = [
  {
    id: 'session-report',
    date: '2026-09',
    title: 'Session Report',
    blurb:
      'A printable report over an event or any set of sessions: summary rows, any logged channel’s '
      + 'statistics with thresholds and flag limits, corner speeds and sector times, with trend and '
      + 'distribution charts. Save the layout as a template and reuse it for every event. On the '
      + 'Sessions page, tick sessions — or an event’s header checkbox — and press Report.',
    icon: 'FileText',
    help: { page: 'report', panel: null },
  },
  {
    id: 'video-studio',
    date: '2026-09',
    title: 'Video Studio',
    beta: true,
    blurb:
      'Compose a clip from up to four sessions: onboard video, the 3D view, Track Difference, speed, '
      + 'any telemetry channel, time delta and lap labels, each placed where you want it, aligned by '
      + 'position on track or run as a race. Rendered to MP4 in the background. Open it from the pages '
      + 'menu, or press Create in Video Studio in a loaded session’s toolbar to carry your laps across.',
    icon: 'Film',
    help: { page: 'video-studio', panel: null },
  },
  {
    id: 'track-difference',
    date: '2026-09',
    title: 'Track Difference',
    blurb:
      'Two laps drawn on the circuit they were driven on, with a colored ribbon standing off the road: '
      + 'the color says which lap is ahead, the height says by how much. Laps are matched by position '
      + 'rather than by the clock, so the loss shows up on the piece of road where it happened. '
      + 'Open a session and pick View → Track Difference.',
    icon: 'Route',
    help: { page: 'sessions', panel: 'trackdifference' },
    poster: '/tda-3d-trackdiff-poster.jpg',
    video: ['/tda-3d-trackdiff.webm', '/tda-3d-trackdiff.mp4'],
  },
  {
    id: 'split-times-multi',
    date: '2026-08',
    title: 'Split times across sessions',
    blurb:
      'Add comparison sessions and the Split Times panel ranks every lap from all of them in one table, '
      + 'grouped by session, with best and median across the merged set — and a PNG button that redraws '
      + 'the whole table as an image. View → Split Times.',
    icon: 'Table2',
    help: { page: 'sessions', panel: 'sectortimes' },
  },
  {
    id: 'video-compare-3d',
    date: '2026-08',
    title: 'Side-by-side video export, with the 3D view',
    blurb:
      'Render two to four laps’ videos side by side as one MP4, aligned to the same point on track, '
      + 'with the ghost race tiled in alongside them. The ⇆⬇ button in the Video panel header opens it.',
    icon: 'Clapperboard',
    help: { page: 'sessions', panel: 'videopanel' },
  },
  {
    id: 'time-delta-png',
    date: '2026-08',
    title: 'PNG export from Time Delta',
    blurb:
      'Download the time-delta chart as a 1920×1080 image, complete with the base lap, a legend of every '
      + 'driver and lap time, and the largest gaps marked. The PNG ⤓ button sits in the panel header.',
    icon: 'ImageDown',
    help: { page: 'sessions', panel: 'timedeltatrace' },
  },
  {
    id: 'lap3d',
    date: '2026-07',
    title: '3D view — race your own ghost',
    blurb:
      'Replay two to four laps at once on the real terrain of the circuit, with the live gap between them '
      + 'on screen. It shows what a trace cannot: where the car actually was on the road. Tick your laps '
      + 'and press 3D in the lap list, or View → 3D View to dock it as a panel.',
    icon: 'Box',
    help: { page: 'sessions', panel: 'lap3d' },
  },
];
